import React, { useState, useEffect } from 'react';
import { collection, query, getDocs, orderBy, limit } from 'firebase/firestore';
import { db } from '../firebase/config';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, ClipboardList, MapPin, Lock, LogIn } from 'lucide-react';
import PublicGateModal from './PublicGateModal';

// 비회원 공개 — 제작의뢰 목록 (상세는 로그인)
function PublicRequests() {
    const navigate = useNavigate();
    const [requests, setRequests] = useState([]);
    const [loading, setLoading] = useState(true);
    const [gate, setGate] = useState(null);

    useEffect(() => {
        (async () => {
            try {
                const snap = await getDocs(query(
                    collection(db, 'openRequests'),
                    orderBy('createdAt', 'desc'),
                    limit(30)
                ));
                setRequests(snap.docs.map(d => ({ id: d.id, ...d.data() })));
            } catch (e) { console.error('제작의뢰 목록 로드 실패:', e); }
            setLoading(false);
        })();
    }, []);

    const fmtDate = (ts) => {
        try {
            const d = ts?.toDate ? ts.toDate() : (ts ? new Date(ts) : null);
            if (!d) return '';
            return `${d.getMonth() + 1}/${d.getDate()}`;
        } catch { return ''; }
    };

    // 실제 의뢰가 없을 때 보여줄 예시
    const sampleRequests = [
        { id: 's1', _sample: true, prosthesisType: '지르코니아 크라운', region: '서울 강남구', quantity: '3본', note: '쉐이드 A2, 급하지 않음' },
        { id: 's2', _sample: true, prosthesisType: '임플란트 보철 (커스텀 어버트먼트)', region: '경기 성남시', quantity: '1본', note: '3Shape 스캔파일 전달 예정' },
        { id: 's3', _sample: true, prosthesisType: '라미네이트', region: '인천 남동구', quantity: '2본', note: '심미 케이스, 포토 첨부' },
        { id: 's4', _sample: true, prosthesisType: '풀지르코니아 브릿지', region: '부산 해운대구', quantity: '3유닛', note: '납기 5일 희망' },
        { id: 's5', _sample: true, prosthesisType: '해외 디자인 의뢰 — 크라운 CAD', region: 'Sydney, AU', quantity: '5본', note: 'exocad, STL 파일 전달' },
        { id: 's6', _sample: true, prosthesisType: '투명교정 장치', region: '대구 수성구', quantity: '상하악', note: '스캔파일 보유' },
    ];
    const cards = requests.length > 0 ? requests : sampleRequests;

    const openGate = () => setGate({
        title: '제작의뢰 상세는 회원 전용이에요',
        desc: '로그인하면 의뢰 내용 전체와 첨부파일을 확인하고 견적을 보낼 수 있습니다.',
    });

    return (
        <div style={S.page}>
            <header style={S.header}>
                <div style={S.headerInner}>
                    <button style={S.backBtn} onClick={() => navigate('/')}>
                        <ArrowLeft size={18} /> 홈
                    </button>
                    <div style={S.headerTitleWrap}>
                        <ClipboardList size={20} color="#3b82f6" />
                        <h1 style={S.headerTitle}>제작의뢰</h1>
                    </div>
                    <button style={S.loginTop} onClick={() => navigate('/login')}>
                        <LogIn size={15} /> 로그인
                    </button>
                </div>
            </header>

            <main style={S.main}>
                <p style={S.lead}>
                    거래처 지정 없이 올라온 제작의뢰입니다. 기공소 회원은 로그인 후 견적을 보낼 수 있어요.
                </p>
                {requests.length === 0 && !loading && (
                    <p style={S.sampleNotice}>아직 등록된 의뢰가 없어 예시를 보여드려요 — 첫 의뢰의 주인공이 되어보세요!</p>
                )}

                {loading ? (
                    <div style={S.emptyBox}>불러오는 중...</div>
                ) : (
                    <div style={S.grid}>
                        {cards.map(r => (
                            <div key={r.id} className="pr-card" style={S.card} onClick={openGate}>
                                <div style={S.cardTop}>
                                    <span style={S.badge}>{r._sample ? '예시' : '모집중'}</span>
                                    <span style={S.date}>{r._sample ? '' : fmtDate(r.createdAt)}</span>
                                </div>
                                <h3 style={S.cardTitle}>{r.prosthesisType || r.title || '보철 제작 의뢰'}</h3>
                                <div style={S.meta}>
                                    <span style={S.metaItem}><MapPin size={13} /> {r.region || '지역 비공개'}</span>
                                    {r.quantity && <span style={S.metaItem}>수량 {r.quantity}</span>}
                                </div>
                                {r.note && <p style={S.note}>{r.note}</p>}
                                <div style={S.lockRow}><Lock size={13} /> 상세 내용은 로그인 후 확인</div>
                            </div>
                        ))}
                    </div>
                )}
            </main>

            <PublicGateModal gate={gate} onClose={() => setGate(null)} />
            <style>{`
                .pr-card { transition: transform 0.15s ease, box-shadow 0.15s ease; }
                .pr-card:hover { transform: translateY(-3px); box-shadow: 0 10px 28px rgba(15,23,42,0.12) !important; }
            `}</style>
        </div>
    );
}

const S = {
    page: { minHeight: '100vh', backgroundColor: '#f8fafc' },
    header: { position: 'sticky', top: 0, zIndex: 50, backgroundColor: 'rgba(255,255,255,0.92)', backdropFilter: 'blur(10px)', borderBottom: '1px solid #e2e8f0' },
    headerInner: { maxWidth: 1100, margin: '0 auto', padding: '0 20px', height: 60, display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12 },
    backBtn: { display: 'flex', alignItems: 'center', gap: 6, background: 'none', border: 'none', fontSize: 14, fontWeight: 600, color: '#475569', cursor: 'pointer', padding: '8px 10px' },
    headerTitleWrap: { display: 'flex', alignItems: 'center', gap: 8 },
    headerTitle: { margin: 0, fontSize: 18, fontWeight: 800, color: '#0f172a' },
    loginTop: { display: 'flex', alignItems: 'center', gap: 6, padding: '9px 16px', backgroundColor: '#3b82f6', color: '#fff', border: 'none', borderRadius: 10, fontSize: 13, fontWeight: 700, cursor: 'pointer' },

    main: { maxWidth: 1100, margin: '0 auto', padding: '32px 20px 80px' },
    lead: { margin: '0 0 8px', fontSize: 15, color: '#64748b', lineHeight: 1.6 },
    sampleNotice: { margin: '0 0 20px', fontSize: 13, color: '#94a3b8' },
    grid: { display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))', gap: 16, marginTop: 20 },
    card: { padding: 20, backgroundColor: '#fff', border: '1px solid #e2e8f0', borderRadius: 14, cursor: 'pointer', boxShadow: '0 1px 3px rgba(0,0,0,0.05)' },
    cardTop: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 },
    badge: { padding: '4px 10px', backgroundColor: '#ecfdf5', color: '#059669', borderRadius: 999, fontSize: 12, fontWeight: 700 },
    date: { fontSize: 12, color: '#94a3b8' },
    cardTitle: { margin: '0 0 10px', fontSize: 16, fontWeight: 700, lineHeight: 1.4, color: '#0f172a' },
    meta: { display: 'flex', gap: 12, flexWrap: 'wrap', marginBottom: 8 },
    metaItem: { display: 'inline-flex', alignItems: 'center', gap: 4, fontSize: 13, color: '#64748b' },
    note: { margin: '0 0 14px', fontSize: 13, color: '#94a3b8', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' },
    lockRow: { display: 'flex', alignItems: 'center', gap: 6, paddingTop: 12, borderTop: '1px dashed #e2e8f0', fontSize: 12, color: '#94a3b8' },
    emptyBox: { padding: '60px 20px', textAlign: 'center', color: '#94a3b8', fontSize: 14 },
};

export default PublicRequests;
